// ─── Login / Sign-Up Modal ──────────────────────────────────────────────────
// Email + password form with social OAuth buttons (Google, Spotify).
// Avatar dropdown menu when logged in.

import { toast } from "sonner";
import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import {
  signIn,
  signUp,
  signInWithGoogle,
  signInWithSpotify,
  signOut,
  onAuthStateChange,
  type User,
} from "../services/authService";
import { reportError } from "../services/errorService";
import { LogIn, LogOut, User as UserIcon, Train } from "../data/design-tokens";

// ─── SVG brand icons ──────────────────────────────────────────────

function GoogleIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}


function SpotifyIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1DB954">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
    </svg>
  );
}

// ─── Provider button ────────────────────────────────────────────────────────

function ProviderButton({ icon, label, onClick, loading }: Readonly<{
  icon: any;
  label: string;
  onClick: () => void;
  loading: boolean;
}>) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="relative flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md active:scale-[0.98] disabled:opacity-50"
    >
      <span className="absolute left-4 flex size-5 items-center justify-center">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ─── Auth hook ──────────────────────────────────────────────────────────────

// Re-export useAuth from the shared AuthContext so existing imports
// (e.g. App.tsx) continue to work without changes.
export { useAuth } from "../contexts/AuthContext";

// ─── Login Modal ────────────────────────────────────────────────────────────

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LoginModal({ open, onOpenChange }: Readonly<LoginModalProps>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const submitLabel = mode === "login" ? "Log in" : "Create account";

  // Auto-close modal when user signs in (works for both OAuth redirect and email)
  useEffect(() => {
    if (!open) return;
    const unsubscribe = onAuthStateChange((session) => {
      if (session?.user) {
        onOpenChange(false);
        setBusy(false);
        setError(null);
        setEmail("");
        setPassword("");
      }
    });
    return unsubscribe;
  }, [open, onOpenChange]);

  async function handleOAuth(fn: () => Promise<{ error: string | null }>) {
    try {
      setBusy(true);
      setError(null);
      const result = await fn();
      if (result.error) {
        setError(result.error);
        setBusy(false);
      }
      // On success the page redirects via OAuth — no need to clear busy
    } catch (err: any) {
      reportError(err, { level: "critical", context: { op: "OAuth" } });
      setError(err?.message || "An unexpected error occurred");
      setBusy(false);
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = mode === "login" ? await signIn(email.trim(), password) : await signUp(email.trim(), password);
      if (result.error) setError(result.error);
      else if ("needsConfirmation" in result && result.needsConfirmation) {
        toast.info("Check your email to confirm your account, then sign in.");
        setMode("login");
        setPassword("");
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "An unexpected error occurred");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-center text-lg">Welcome to Via Fera</DialogTitle>
          <DialogDescription className="text-center">
            Sign in to save trips, sync across devices, and unlock personalized recommendations.
          </DialogDescription>
        </DialogHeader>

        {/* Email + Password Form */}
        <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3 pt-2">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            autoComplete="email"
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:opacity-50"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={busy || !email.trim() || !password.trim()}
            className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-50"
          >
            {busy ? "Please wait\u2026" : submitLabel}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400">
          {mode === "login" ? (
            <>Don't have an account?{" "}
              <button type="button" onClick={() => { setMode("signup"); setError(null); }} className="text-indigo-600 hover:underline font-medium">Sign up</button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button type="button" onClick={() => { setMode("login"); setError(null); }} className="text-indigo-600 hover:underline font-medium">Log in</button>
            </>
          )}
        </p>

        {/* Divider */}
        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400">or</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* OAuth Providers */}
        <div className="flex flex-col gap-3">
          <ProviderButton
            icon={<GoogleIcon className="size-5" />}
            label="Continue with Google"
            onClick={() => handleOAuth(signInWithGoogle)}
            loading={busy}
          />
          <ProviderButton
            icon={<SpotifyIcon className="size-5" />}
            label="Continue with Spotify"
            onClick={() => handleOAuth(signInWithSpotify)}
            loading={busy}
          />
        </div>

        {error && (
          <p className="text-center text-xs text-red-500 mt-1">{error}</p>
        )}


      </DialogContent>
    </Dialog>
  );
}

// ─── Avatar with fallback ────────────────────────────────────────────────────

function AvatarImg({ src }: Readonly<{ src: string }>) {
  const [err, setErr] = useState(false);
  const onError = useCallback(() => setErr(true), []);
  if (err) return <UserIcon className="size-4" />;
  return (
    <img
      src={src}
      alt=""
      className="size-5 rounded-full"
      referrerPolicy="no-referrer"
      onError={onError}
    />
  );
}

// ─── Auth Button (header) ───────────────────────────────────────────────────

interface AuthButtonProps {
  user: User | null;
  onLoginClick: () => void;
  trainEnabled?: boolean;
  onTrainToggle?: (enabled: boolean) => void;
}

export function AuthButton({ user, onLoginClick, trainEnabled, onTrainToggle }: Readonly<AuthButtonProps>) {

  if (user) {
    const avatarUrl = (user.user_metadata?.avatar_url || user.user_metadata?.picture) as string | undefined;
    const name = (user.user_metadata?.full_name || user.user_metadata?.name || user.email || "Account") as string;
    const firstName = name.split(" ")[0];

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white/95 px-3 py-2 text-[12px] font-medium text-gray-700 shadow-lg transition-all hover:bg-gray-50"
            title={`Signed in as ${name}`}
          >
            {avatarUrl ? (
              <AvatarImg src={avatarUrl} />
            ) : (
              <UserIcon className="size-4" />
            )}
            <span className="hidden sm:inline whitespace-nowrap">{firstName}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={6} className="w-[320px]">
          <DropdownMenuLabel>{name}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onTrainToggle?.(!trainEnabled)}
            onSelect={(e) => e.preventDefault()}
          >
            <Train className="size-4" />
            Train Animation
            <span className="ml-auto text-[10px] opacity-60">{trainEnabled ? "ON" : "OFF"}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <iframe
            src="https://ko-fi.com/viafera/?hidefeed=true&widget=true&embed=true&preview=true"
            className="w-full border-none rounded bg-[#f9f9f9] p-1"
            height={712}
            title="Support Via Fera on Ko-fi"
          />
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => signOut()}>
            <LogOut className="size-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <button
      onClick={onLoginClick}
      className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white/95 px-3 py-2 text-[12px] font-medium text-gray-700 shadow-lg transition-all hover:bg-gray-50"
      title="Log in or create an account"
    >
      <LogIn className="size-4" />
      <span className="hidden sm:inline">Log in</span>
    </button>
  );
}
